import React, { forwardRef } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import {
  Block,
  Checkbox,
  CheckboxGroup,
  CheckboxGroupProps,
  ExternalLink,
  Heading,
  InlineAlert,
  Paragraph,
  Text,
} from 'suomifi-ui-components'
import {
  isEidasIdentifiedUser,
  isSameHetuOrEidasPID,
} from 'todistukset-common/src/vtj/user.utils'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import CertificateTypes, {
  isVtjOteTodistus,
  VtjOteTodistusType,
} from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { useFirstErrorImperativeHandle } from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import {
  hasDependants,
  isDependantRestricted,
} from 'todistukset-common/src/vtj/dependant-helpers'
import fp from 'lodash/fp'
import getFormActions from 'todistukset-ui/src/vtj/stores/actions/get-form-actions'
import { hasName, reversedDisplayNameOf } from 'common/src/vtj/name.util'
import {
  ContentSelectionWrapper,
  PersonSelectionWrapper,
  PreviousAddressesPeriodTextInput,
} from 'todistukset-ui/src/vtj/components/order-wizard/BasicInformationStep.sc'
import {
  FormContainer,
  ParagraphWithNewlines,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import TranslationFormWithPreviousAddressesInlineAlert from 'todistukset-ui/src/vtj/components/order-wizard/TranslationFormWithPreviousAddressesInlineAlert.react'
import { validatePersons } from 'todistukset-common/src/vtj/form-validation.util'
import { getValidationErrorFromValidator } from 'todistukset-ui/src/vtj/components/order-wizard/validation'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import type { Huollettava } from 'todistukset-common/src/vtj/vtj-kysely/huoltod-soap-response-converter'
import { getEnabledFeatures } from 'todistukset-common/src/vtj/certificate.util'
import { CertificateFeature } from 'todistukset-common/src/vtj/CertificateFeature.enum'
import { TodistusPersonRole } from 'todistukset-common/src/vtj/types/todistus-person.type'
import { getIncludeOptionsFromTodistusPyynto } from 'todistukset-common/src/vtj/util/include-options.util'

const BasicInformationStep: StepComponent = observer(
  forwardRef(function BasicInformationStep({ titleId, certificateType }, ref) {
    if (!isVtjOteTodistus(certificateType)) {
      throw new Error(
        `Type check failed. This step supports only VtjOteTodistusType but ${certificateType} was given.`
      )
    }

    const {
      common: { loggedInUser },
      forms: { [certificateType]: form },
    } = useStore()

    const user = loggedInUser.get()

    const shouldRenderPersonSelectionSection =
      certificateType !== CertificateTypes.AVIOLIITTOTODISTUS &&
      certificateType !== CertificateTypes.SIVIILISAATYTODISTUS &&
      hasDependants(user)

    const refs = useFirstErrorImperativeHandle(
      ref,
      form,
      function (form) {
        return {
          personSelection: getValidationErrorFromValidator(
            validatePersons,
            form.persons,
            form
          ),
        }
      },
      ['personSelection']
    )

    function mainDescriptionKey(
      certificateType: VtjOteTodistusType,
      isEidas: boolean
    ) {
      const hasEidasKey: { [k: string]: boolean } = {
        [CertificateTypes.ASUINPAIKKATODISTUS]: true,
        [CertificateTypes.SYNTYMATODISTUS]: true,
      }
      if (isEidas && hasEidasKey[certificateType]) {
        return `orderWizardBasicInformationStepMainDescription_eidas_${certificateType}`
      }
      return `orderWizardBasicInformationStepMainDescription_${certificateType}`
    }

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="orderWizardBasicInformationStepMainTitle" />
          </Heading>
          <ParagraphWithNewlines>
            <LocalizedString
              stringKey={mainDescriptionKey(
                certificateType,
                isEidasIdentifiedUser(user)
              )}
            />
          </ParagraphWithNewlines>
        </Block>
        {shouldRenderPersonSelectionSection && (
          <Block variant="section" data-test-id="person-selection-section">
            <Heading variant="h3">
              <LocalizedString stringKey="orderWizardBasicInformationStepPersonSelectionTitle" />
            </Heading>
            <Paragraph>
              <LocalizedString stringKey="orderWizardBasicInformationStepPersonSelectionText" />
            </Paragraph>
            <PersonSelection
              certificateType={certificateType}
              ref={refs.personSelection}
            />
          </Block>
        )}
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="orderWizardBasicInformationStepTodistusContentTitle" />
          </Heading>
          <ParagraphWithNewlines>
            <LocalizedString
              stringKey={`orderWizardBasicInformationStepTodistusContentText_${certificateType}`}
            />
          </ParagraphWithNewlines>
          <ContentSelection certificateType={certificateType} />
        </Block>
      </FormContainer>
    )
  })
)

const PersonSelection: React.FC<
  {
    certificateType: VtjOteTodistusType
  } & React.RefAttributes<HTMLInputElement>
> = observer(
  forwardRef(function PersonSelection({ certificateType }, firstErrorRef) {
    const {
      common,
      forms,
      steps: {
        [certificateType]: { showStepValidationErrors },
      },
    } = useStore()
    const user = common.loggedInUser.get()
    const form = forms[certificateType]
    const { t } = useTranslation()

    const dependants = user.huollettavat ?? []
    const sortedDependants = fp.sortBy(isDependantRestricted, dependants)
    const hasRestrictedDependants = dependants.some(isDependantRestricted)

    const error = getValidationErrorFromValidator(
      validatePersons,
      form.persons,
      form
    )
    const groupStatus: CheckboxGroupProps['groupStatus'] =
      showStepValidationErrors && error ? 'error' : 'default'
    return (
      <PersonSelectionWrapper>
        <CheckboxGroup
          labelText={
            <LocalizedString stringKey="orderWizardBasicInformationStepPersonSelectionApplicantTitle" />
          }
          groupStatus={groupStatus}
          groupStatusText={
            showStepValidationErrors && error
              ? t(error.errorTranslationKey)
              : undefined
          }
        >
          {[
            <SubjectSelectionCheckbox
              key={0}
              certificateType={certificateType}
              subject={user}
              subjectIndex={0}
              ref={firstErrorRef}
            />,
          ]}
        </CheckboxGroup>

        <CheckboxGroup
          labelText={
            <LocalizedString stringKey="orderWizardBasicInformationStepPersonSelectionDepedantsTitle" />
          }
          groupStatus={groupStatus}
          groupStatusText={
            showStepValidationErrors && error
              ? t(error.errorTranslationKey)
              : undefined
          }
        >
          {sortedDependants.map((dependant, index) => {
            return (
              <SubjectSelectionCheckbox
                key={index}
                certificateType={certificateType}
                subject={dependant}
                subjectIndex={index}
                isDependant
                disabled={isDependantRestricted(dependant)}
              />
            )
          })}
        </CheckboxGroup>

        {hasRestrictedDependants && (
          <InlineAlert
            labelText={
              <LocalizedString stringKey="orderWizardBasicInformationStepPersonSelectionRestrictedDependantAlertLabel" />
            }
            ariaLiveMode="polite"
            data-test-id="restricted-dependant-alert"
          >
            <Paragraph>
              <LocalizedString stringKey="orderWizardBasicInformationStepPersonSelectionRestrictedDependantAlertText" />
            </Paragraph>
            <ExternalLink
              href={t(
                'orderWizardBasicInformationStepPersonSelectionRestrictedDependantAlertLinkHref'
              )}
              labelNewWindow={t('opensInNewWindow')}
              underline="initial"
            >
              <LocalizedString stringKey="orderWizardBasicInformationStepPersonSelectionRestrictedDependantAlertLinkText" />
            </ExternalLink>
          </InlineAlert>
        )}
      </PersonSelectionWrapper>
    )
  })
)

const SubjectSelectionCheckbox: React.FC<
  {
    certificateType: VtjOteTodistusType
    subject: TodistuksetUser | Huollettava
    subjectIndex: number
    isDependant?: boolean
    disabled?: boolean
  } & React.RefAttributes<HTMLInputElement>
> = observer(
  forwardRef(function SubjectSelectionCheckbox(
    {
      certificateType,
      subject,
      subjectIndex,
      isDependant = false,
      disabled = false,
    },
    ref
  ) {
    const store = useStore()
    const { toggleSubject } = getFormActions(certificateType)

    const persons = store.forms[certificateType].persons
    const person = persons.find((person) =>
      isSameHetuOrEidasPID(person, subject)
    )
    const isSelected =
      person?.roles.includes(TodistusPersonRole.KOHDEHENKILO) ?? false

    return (
      <Checkbox
        checked={isSelected}
        onClick={() => toggleSubject(subject)}
        disabled={disabled}
        data-test-id={
          isDependant
            ? `dependant-selection-${subjectIndex}`
            : 'option-order-own-certificate'
        }
        ref={ref}
      >
        <Text>
          {hasName(subject) ? (
            reversedDisplayNameOf(subject)
          ) : (
            <LocalizedString stringKey="dependantNameNotAvailable" />
          )}
        </Text>
      </Checkbox>
    )
  })
)

const ContentSelection: React.FC<{ certificateType: VtjOteTodistusType }> =
  observer(({ certificateType }) => {
    const { forms, common } = useStore()
    const form = forms[certificateType]
    const user = common.loggedInUser.get()

    const certificateFeatures = getEnabledFeatures({
      certificateType,
      isAdult: user.isAdult,
    })

    const {
      includeHetu,
      includeMaritalStatus,
      includePreviousPermanentAddresses,
      includePreviousMarriages,
      includePreviousNames,
      includeSpousePreviousNames,
      previousAddressesOverYears,
    } = getIncludeOptionsFromTodistusPyynto(form)

    const {
      setPreviousAddressesOverYears,
      toggleIncludePreviousNames,
      toggleIncludeSpousePreviousNames,
      toggleIncludeHetu,
      toggleIncludeMaritalStatus,
      toggleIncludePreviousAddresses,
      toggleIncludePreviousMarriages,
    } = getFormActions(certificateType)

    return (
      <ContentSelectionWrapper>
        <CheckboxGroup
          labelText={
            <LocalizedString stringKey="orderWizardBasicInformationStepTodistusContentTitle" />
          }
          labelMode="hidden"
        >
          {certificateFeatures.includes(
            CertificateFeature.INCLUDE_PREVIOUS_PERMANENT_ADDRESSES
          ) && (
            <>
              <Checkbox
                data-test-id="option-show-previous-addresses"
                checked={includePreviousPermanentAddresses}
                onClick={() => toggleIncludePreviousAddresses()}
              >
                <Text>
                  <LocalizedString stringKey="orderWizardBasicInformationStepContentSelectionPreviousAddressesLabel" />
                </Text>
              </Checkbox>
              {form.includePreviousPermanentAddresses && (
                <PreviousAddressesPeriodTextInput
                  labelText={
                    <LocalizedString stringKey="orderWizardBasicInformationStepContentSelectionPreviousAddressesHistoryLengthLabel" />
                  }
                  data-test-id="input-address-history-length"
                  type="number"
                  value={previousAddressesOverYears}
                  onChange={(value) => setPreviousAddressesOverYears(value)}
                />
              )}
              <TranslationFormWithPreviousAddressesInlineAlert
                certificateType={certificateType}
              />
            </>
          )}

          {/* In syntymätodistus, previous names should always be included when ordering
           * from self service. It still needs to be included in certificate features,
           * because it is optional in virkailija-avaus syntymätodistus and other self
           * service certificates. */}
          {certificateType !== CertificateTypes.SYNTYMATODISTUS &&
            certificateFeatures.includes(
              CertificateFeature.INCLUDE_PREVIOUS_NAMES
            ) && (
              <Checkbox
                data-test-id="option-show-previous-names"
                checked={includePreviousNames}
                onClick={() => toggleIncludePreviousNames()}
              >
                <Text>
                  <LocalizedString
                    stringKey={`orderWizardBasicInformationStepContentSelectionPreviousNamesLabel_${certificateType}`}
                  />
                </Text>
              </Checkbox>
            )}

          {certificateFeatures.includes(
            CertificateFeature.INCLUDE_SPOUSE_PREVIOUS_NAMES
          ) && (
            <Checkbox
              data-test-id="option-show-spouse-previous-names"
              checked={includeSpousePreviousNames}
              onClick={() => toggleIncludeSpousePreviousNames()}
            >
              <Text>
                <LocalizedString stringKey="orderWizardBasicInformationStepContentSelectionSpousePreviousNamesLabel" />
              </Text>
            </Checkbox>
          )}

          <Checkbox
            data-test-id="option-show-ssn"
            checked={includeHetu}
            onClick={() => toggleIncludeHetu()}
          >
            <Text>
              <LocalizedString stringKey="orderWizardBasicInformationStepContentSelectionHetuLabel" />
            </Text>
          </Checkbox>

          {certificateFeatures.includes(
            CertificateFeature.INCLUDE_MARITAL_STATUS
          ) && (
            <Checkbox
              data-test-id="option-show-marital-status"
              checked={includeMaritalStatus}
              onClick={() => toggleIncludeMaritalStatus()}
            >
              <Text>
                <LocalizedString stringKey="orderWizardBasicInformationStepContentMaritalStatusLabel" />
              </Text>
            </Checkbox>
          )}

          {certificateFeatures.includes(
            CertificateFeature.INCLUDE_PREVIOUS_MARRIAGES
          ) && (
            <Checkbox
              data-test-id="option-show-previous-marriages"
              checked={includePreviousMarriages}
              onClick={() => toggleIncludePreviousMarriages()}
            >
              <Text>
                <LocalizedString stringKey="orderWizardBasicInformationStepContentPreviousMarriagesLabel" />
              </Text>
            </Checkbox>
          )}
        </CheckboxGroup>
      </ContentSelectionWrapper>
    )
  })

export default BasicInformationStep
